import React, { useState } from "react";
import { toast } from "react-toastify";
import { authService } from "../services/authService";

const RegistrationPage = ({ onRegistrationSuccess }) => {
  const [accountType, setAccountType] = useState("user"); // 'user' or 'business'
  const [formData, setFormData] = useState({
    // Common fields
    username: "",
    email: "",
    password: "",
    confirmPassword: "",

    // Regular user fields
    firstName: "",
    lastName: "",
    phoneNumber: "",
    dateOfBirth: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "USA",
    },

    // Business fields
    businessName: "",
    businessType: "",
    businessDescription: "",
    businessPhone: "",
    website: "",
    businessAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "USA",
    },
    contactPerson: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested objects
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      if (accountType === "user") {
        // Register regular user
        await authService.registerRegularUser({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          dateOfBirth: formData.dateOfBirth,
          address: formData.address,
        });
      } else {
        // Register business
        await authService.registerBusiness({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          businessName: formData.businessName,
          businessType: formData.businessType,
          businessDescription: formData.businessDescription,
          businessPhone: formData.businessPhone,
          website: formData.website,
          businessAddress: formData.businessAddress,
          contactPerson: formData.contactPerson,
        });
      }

      toast.success("Registration successful! Please log in.");
      onRegistrationSuccess();
    } catch (error) {
      console.error("Registration error:", error);

      if (error.response?.data?.errors) {
        // Handle validation errors
        error.response.data.errors.forEach((err) => {
          toast.error(err.msg);
        });
      } else {
        toast.error(error.response?.data?.message || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    "Restaurant",
    "Grocery Store",
    "Retail",
    "Service Provider",
    "Healthcare",
    "Education",
    "Technology",
    "Manufacturing",
    "Other",
  ];

  return (
    <div
      className="container"
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        minHeight: "100vh",
        padding: "20px 0",
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: "700px",
          margin: "20px auto",
          borderRadius: "20px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          border: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "30px",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              margin: "0 0 10px 0",
              fontSize: "32px",
              fontWeight: "700",
            }}
          >
            🚀 Join Us Today
          </h1>
          <p
            style={{
              margin: "0",
              fontSize: "16px",
              opacity: "0.9",
            }}
          >
            Create your account and get started
          </p>
        </div>

        <div style={{ padding: "40px" }}>
          {/* Account Type Selection */}
          <div className="form-group" style={{ marginBottom: "30px" }}>
            <label
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#2c3e50",
                marginBottom: "15px",
                display: "block",
              }}
            >
              Choose Account Type:
            </label>
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "center",
              }}
            >
              <label
                style={{
                  flex: "1",
                  display: "flex",
                  alignItems: "center",
                  padding: "20px",
                  border: `3px solid ${
                    accountType === "user" ? "#667eea" : "#e9ecef"
                  }`,
                  borderRadius: "15px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor: accountType === "user" ? "#f8f9ff" : "white",
                  transform:
                    accountType === "user" ? "scale(1.02)" : "scale(1)",
                }}
              >
                <input
                  type="radio"
                  value="user"
                  checked={accountType === "user"}
                  onChange={(e) => setAccountType(e.target.value)}
                  style={{ display: "none" }}
                />
                <div style={{ textAlign: "center", width: "100%" }}>
                  <div style={{ fontSize: "32px", marginBottom: "10px" }}>
                    👤
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: accountType === "user" ? "#667eea" : "#6c757d",
                      fontSize: "16px",
                    }}
                  >
                    Regular User
                  </div>
                </div>
              </label>

              <label
                style={{
                  flex: "1",
                  display: "flex",
                  alignItems: "center",
                  padding: "20px",
                  border: `3px solid ${
                    accountType === "business" ? "#667eea" : "#e9ecef"
                  }`,
                  borderRadius: "15px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backgroundColor:
                    accountType === "business" ? "#f8f9ff" : "white",
                  transform:
                    accountType === "business" ? "scale(1.02)" : "scale(1)",
                }}
              >
                <input
                  type="radio"
                  value="business"
                  checked={accountType === "business"}
                  onChange={(e) => setAccountType(e.target.value)}
                  style={{ display: "none" }}
                />
                <div style={{ textAlign: "center", width: "100%" }}>
                  <div style={{ fontSize: "32px", marginBottom: "10px" }}>
                    🏢
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: accountType === "business" ? "#667eea" : "#6c757d",
                      fontSize: "16px",
                    }}
                  >
                    Business
                  </div>
                </div>
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ marginTop: "20px" }}>
            {/* Common Fields */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "25px",
              }}
            >
              <div className="form-group">
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#2c3e50",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Username:
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  minLength="3"
                  maxLength="30"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e9ecef",
                    borderRadius: "10px",
                    fontSize: "16px",
                    transition: "all 0.3s ease",
                    backgroundColor: "#f8f9fa",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.backgroundColor = "white";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102,126,234,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e9ecef";
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div className="form-group">
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#2c3e50",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Email:
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e9ecef",
                    borderRadius: "10px",
                    fontSize: "16px",
                    transition: "all 0.3s ease",
                    backgroundColor: "#f8f9fa",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.backgroundColor = "white";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(102,126,234,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e9ecef";
                    e.target.style.backgroundColor = "#f8f9fa";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label>Confirm Password:</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                minLength="6"
              />
            </div>

            {/* Regular User Fields */}
            {accountType === "user" && (
              <>
                <h3>Personal Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>First Name:</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name:</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone Number:</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date of Birth:</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </div>

                <h3>Address</h3>
                <div className="form-group">
                  <label>Street Address:</label>
                  <input
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City:</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>State:</label>
                    <input
                      type="text"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code:</label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Business Fields */}
            {accountType === "business" && (
              <>
                <h3>Business Information</h3>

                <div className="form-group">
                  <label>Business Name:</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Business Type:</label>
                  <select
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Business Type</option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Business Description:</label>
                  <textarea
                    name="businessDescription"
                    value={formData.businessDescription}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Business Phone:</label>
                  <input
                    type="tel"
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Website:</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                  />
                </div>

                <h3>Business Address</h3>
                <div className="form-group">
                  <label>Street Address:</label>
                  <input
                    type="text"
                    name="businessAddress.street"
                    value={formData.businessAddress.street}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City:</label>
                    <input
                      type="text"
                      name="businessAddress.city"
                      value={formData.businessAddress.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>State:</label>
                    <input
                      type="text"
                      name="businessAddress.state"
                      value={formData.businessAddress.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code:</label>
                    <input
                      type="text"
                      name="businessAddress.zipCode"
                      value={formData.businessAddress.zipCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <h3>Contact Person</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name:</label>
                    <input
                      type="text"
                      name="contactPerson.firstName"
                      value={formData.contactPerson.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name:</label>
                    <input
                      type="text"
                      name="contactPerson.lastName"
                      value={formData.contactPerson.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Email:</label>
                    <input
                      type="email"
                      name="contactPerson.email"
                      value={formData.contactPerson.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact Phone:</label>
                    <input
                      type="tel"
                      name="contactPerson.phone"
                      value={formData.contactPerson.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group" style={{ marginTop: "30px" }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "16px 24px",
                  fontSize: "18px",
                  fontWeight: "600",
                  borderRadius: "12px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: loading ? "0.7" : "1",
                  transform: loading ? "scale(0.98)" : "scale(1)",
                }}
                onMouseOver={(e) => {
                  if (!loading) {
                    e.target.style.transform = "scale(1.02)";
                    e.target.style.boxShadow =
                      "0 8px 25px rgba(102,126,234,0.4)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading) {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "none";
                  }
                }}
              >
                {loading ? "🔄 Creating Account..." : "🚀 Create Account"}
              </button>
            </div>
          </form>

          <div
            style={{
              textAlign: "center",
              marginTop: "30px",
              padding: "20px",
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
            }}
          >
            <p
              style={{
                margin: "0 0 15px 0",
                color: "#6c757d",
                fontSize: "14px",
              }}
            >
              Already have an account?
            </p>
            <button
              type="button"
              onClick={() => onRegistrationSuccess()}
              style={{
                padding: "10px 30px",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "8px",
                border: "2px solid #667eea",
                backgroundColor: "transparent",
                color: "#667eea",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#667eea";
                e.target.style.color = "white";
                e.target.style.transform = "translateY(-2px)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#667eea";
                e.target.style.transform = "translateY(0)";
              }}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
